import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import { CssBaseline, ThemeProvider } from "@mui/material";
import { DEFAULT_APPEARANCE, getAppearanceTokens, normalizeAppearancePreferences } from "./appearanceTokens";
import { getAppTheme } from "./MuiThemeSetup";
import {
  loadAppearancePreferences,
  resetAppearancePreferences,
  saveAppearancePreferences,
} from "../services/appearancePreferences";

export const AppearanceContext = createContext({
  preferences: DEFAULT_APPEARANCE,
  mode: "light",
  updatePreference: () => {},
  updatePreferences: () => {},
  resetPreferences: () => {},
});

export const ColorModeContext = createContext({
  mode: "light",
  setTheme: () => {},
});

export const useAppearance = () => useContext(AppearanceContext);
export const useColorMode = () => useContext(ColorModeContext);

export default function AppearanceProvider({ userKey = "guest", children }) {
  const [preferences, setPreferences] = useState(() => loadAppearancePreferences(userKey));
  const mode = preferences.mode;
  const resolvedMode = mode === "dark" ? "dark" : "light";
  const updatePreferences = useCallback((changes) => {
    setPreferences((current) => normalizeAppearancePreferences({ ...current, ...changes }));
  }, []);
  const updatePreference = useCallback((key, value) => updatePreferences({ [key]: value }), [updatePreferences]);
  const resetPreferences = useCallback(() => {
    setPreferences(resetAppearancePreferences(userKey));
  }, [userKey]);

  useLayoutEffect(() => {
    const root = document.documentElement;
    root.setAttribute("data-theme", resolvedMode);
    root.setAttribute("data-appearance", mode);
    root.classList.toggle("dark", resolvedMode === "dark");
    root.style.colorScheme = resolvedMode;
    Object.entries(getAppearanceTokens(preferences, resolvedMode).vars).forEach(([name, value]) => {
      root.style.setProperty(name, String(value));
    });
  }, [mode, preferences, resolvedMode]);

  useEffect(() => {
    saveAppearancePreferences(userKey, preferences, resolvedMode);
  }, [preferences, resolvedMode, userKey]);

  const theme = useMemo(() => getAppTheme(resolvedMode, "ltr", preferences), [resolvedMode, preferences]);
  const appearanceValue = useMemo(
    () => ({ preferences, mode, updatePreference, updatePreferences, resetPreferences }),
    [preferences, mode, updatePreference, updatePreferences, resetPreferences],
  );
  const colorModeValue = useMemo(
    () => ({ mode, setTheme: (nextMode) => updatePreference("mode", nextMode) }),
    [mode, updatePreference],
  );

  return (
    <AppearanceContext.Provider value={appearanceValue}>
      <ColorModeContext.Provider value={colorModeValue}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          {children}
        </ThemeProvider>
      </ColorModeContext.Provider>
    </AppearanceContext.Provider>
  );
}
